import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFocusManagement } from '../hooks/useSpatialNavigation';
import QRCode from './QRCode';
import QRCodeModal from './QRCodeModal';
import MediaPlayer from './MediaPlayer';
import PlayerControls from './PlayerControls';
import SettingsModal from './SettingsModal';
import FullscreenControls from './FullscreenControls';
import TVInstructions from './TVInstructions';
import AppStoreLinks from './AppStoreLinks';
import { Album, SlideshowSettings, PlayerState } from '../types';
import { ApiService } from '../services/api';

interface AlbumViewProps {
  album: Album;
}

const AlbumView: React.FC<AlbumViewProps> = ({ album: initialAlbum }) => {
  const [album, setAlbum] = useState<Album>(initialAlbum);
  const [playerState, setPlayerState] = useState<PlayerState>({
    isPlaying: false, // Don't auto-start, wait for user interaction
    currentIndex: 0,
    isFullscreen: false
  });

  const [settings, setSettings] = useState<SlideshowSettings>({
    imageDuration: 3,
    transitionStyle: 'fade',
    showQRCode: true,
    hideHeader: false,
    showQRCodeInFullscreen: true
  });

  const [showSettings, setShowSettings] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { focusFirst, updateFocusableElements } = useFocusManagement();

  // Pause slideshow when settings modal opens
  const handleSettingsClick = () => {
    if (playerState.isPlaying) {
      setPlayerState(prev => ({ ...prev, isPlaying: false }));
    }
    setShowSettings(true);
  };

  // Update browser title
  useEffect(() => {
    document.title = `${album.eventName} - FiestAlbum Live View`;
  }, [album.eventName]);


  // Refresh album data for new media
  const refreshAlbumData = useCallback(async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous refreshes
    
    setIsRefreshing(true);
    try {
      const eventCode = album.shareUrl.split('/').pop();
      if (eventCode) {
        const refreshedData = await ApiService.refreshEventDetails(eventCode);
        console.log('API Response received in AlbumView.tsx (refresh):', refreshedData);
        if (refreshedData) {
          const newAlbum = ApiService.transformApiDataToAlbum(refreshedData);
          console.log('Transformed Album Data (refresh):', newAlbum);
          const hasNewMedia = newAlbum.media.length > album.media.length;
          
          if (hasNewMedia) {
            console.log(`Found ${newAlbum.media.length - album.media.length} new media items!`);
            setAlbum(newAlbum);
          }
        }
      }
    } catch (error) {
      console.warn('Error refreshing album data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, album.shareUrl, album.media.length]);

  // Set up timed refresh when slideshow is playing
  useEffect(() => {
    if (playerState.isPlaying) {
      // Refresh every 30 seconds when slideshow is playing
      refreshIntervalRef.current = setInterval(refreshAlbumData, 30000);
    } else {
      // Clear interval when slideshow is paused
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [playerState.isPlaying, album.media.length, refreshAlbumData]);

  // Refresh when cycling back to first item
  const handleIndexChange = (index: number) => {
    const wasAtLastItem = playerState.currentIndex === album.media.length - 1;
    const isGoingToFirstItem = index === 0;
    
    // If we were at the last item and now going to first, refresh data
    if (wasAtLastItem && isGoingToFirstItem && playerState.isPlaying) {
      console.log('Cycling back to first item - refreshing for new media');
      refreshAlbumData();
    }
    
    setPlayerState(prev => ({ ...prev, currentIndex: index }));
  };

  // Initialize focus management for smart TV
  useEffect(() => {
    updateFocusableElements();
    // Auto-focus first element on smart TV
    if (document.body.classList.contains('smart-tv')) {
      setTimeout(() => focusFirst(), 1000);
    }
  }, [updateFocusableElements, focusFirst]);

  const handlePlay = () => {
    setPlayerState(prev => ({ ...prev, isPlaying: true }));
  };

  const handlePause = () => {
    setPlayerState(prev => ({ ...prev, isPlaying: false }));
  };

  const handleStop = () => {
    setPlayerState(prev => ({ 
      ...prev, 
      isPlaying: false, 
      currentIndex: 0 
    }));
  };

  const handlePrevious = () => {
    setPlayerState(prev => ({
      ...prev,
      currentIndex: prev.currentIndex > 0 
        ? prev.currentIndex - 1 
        : album.media.length - 1
    }));
  };

  const handleNext = () => {
    setPlayerState(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % album.media.length
    }));
  };


  const handleVideoEnd = () => {
    handleNext();
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!playerState.isFullscreen) {
        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setPlayerState(prev => ({
        ...prev,
        isFullscreen: !!document.fullscreenElement
      }));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Check if album is empty
  const isAlbumEmpty = album.media.length === 0;

  return (
    <div 
      ref={containerRef}
      className={`album-view ${playerState.isFullscreen ? 'fullscreen' : ''} ${settings.hideHeader ? 'header-hidden' : ''}`}
    >
      {/* Header */}
      {!settings.hideHeader && (
        <div className="album-header">
          <div className="header-left">
            <FullscreenControls
              isFullscreen={playerState.isFullscreen}
              onToggleFullscreen={toggleFullscreen}
            />
          </div>
          <div className="event-name">
            {album.eventName}
            {isRefreshing && (
              <span className="refresh-indicator" title="Checking for new media...">
                🔄
              </span>
            )}
          </div>
          <div className="header-right">
            <button 
              className="qr-toggle-btn"
              onClick={() => setShowQRModal(true)}
              title="Show QR Code"
            >
              QR Code
            </button>
            <AppStoreLinks className="header-app-store-links" />
            <div className="header-fiestalbum-logo">
              <img 
                src="/fiestalbum alpha.gif" 
                alt="FiestAlbum" 
                className="fiestalbum-logo"
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="album-content">
        {/* QR Code Section */}
        {settings.showQRCode && !playerState.isFullscreen && (
          <div className="qr-section">
            <QRCode 
              url={album.shareUrl} 
              size={150}
              className="album-qr"
              qrCodeUrl={album.qrCodeUrl}
            />
          </div>
        )}
        
        {/* Fullscreen QR Code Text Button */}
        {playerState.isFullscreen && settings.showQRCodeInFullscreen && (
          <div className="qr-section-fullscreen">
            <button 
              className="qr-code-text-btn"
              onClick={() => setShowQRModal(true)}
              title="Click to view QR Code"
            >
              QR Code
            </button>
          </div>
        )}

        {/* Media Section */}
        <div className="media-section">
          <div className="media-container">
            {/* Fullscreen Button when header is hidden */}
            {settings.hideHeader && (
              <div className="media-fullscreen-controls">
                <FullscreenControls
                  isFullscreen={playerState.isFullscreen}
                  onToggleFullscreen={toggleFullscreen}
                />
              </div>
            )}
            
            {isAlbumEmpty ? (
              /* Empty Album Message */
              <div className="empty-album-message">
                <div className="empty-album-icon">📷</div>
                <h2>Album is Empty</h2>
                <p>This event album doesn't have any media yet.</p>
                <p>Please ask the organizer to add photos and videos to the event album.</p>
                <div className="empty-album-event-info">
                  <strong>Event:</strong> {album.eventName}
                </div>
              </div>
            ) : (
              /* Normal Media Player */
              <>
                <MediaPlayer
                  media={album.media}
                  settings={settings}
                  isPlaying={playerState.isPlaying}
                  currentIndex={playerState.currentIndex}
                  onIndexChange={handleIndexChange}
                  onVideoEnd={handleVideoEnd}
                />
                
                <PlayerControls
                  isPlaying={playerState.isPlaying}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onStop={handleStop}
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                  currentIndex={playerState.currentIndex}
                  totalItems={album.media.length}
                  onSettingsClick={handleSettingsClick}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        onSettingsChange={setSettings}
      />

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        url={album.shareUrl}
        qrCodeUrl={album.qrCodeUrl}
      />

      {/* TV Instructions */}
      <TVInstructions />
    </div>
  );
};

export default AlbumView;
